import { ModuleTree } from '@/components/ModuleTree';
import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { MODULE_IDS, MODULE_LABELS, type ModuleId } from '@/lib/modules';
import type { AdminUser } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ users: AdminUser[] }>('/api/admin/users');
      setUsers(data.users);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id: number) {
    if (!confirm('Opravdu smazat tohoto uživatele?')) return;
    try {
      await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Uživatel smazán', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Uživatelé</h1>
          <p className="text-sm text-text-secondary mt-0.5">Správa přístupů do administrace</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
        >
          Přidat uživatele
        </button>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg">
              <Th>Email</Th>
              <Th>Jméno</Th>
              <Th>Moduly</Th>
              <Th>Vytvořen</Th>
              <Th>Poslední login</Th>
              <Th>Stav</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody>
            {users == null ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-border-light hover:bg-surface-hover transition-colors"
                >
                  <td className="px-5 py-3 text-text">{u.email}</td>
                  <td className="px-5 py-3 text-text">{u.name}</td>
                  <td className="px-5 py-3">
                    <ModulesBadges modules={u.modules} />
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{fmtDateTime(u.created_at)}</td>
                  <td className="px-5 py-3 text-text-secondary">{fmtDateTime(u.last_login_at)}</td>
                  <td className="px-5 py-3">
                    {u.disabled_at ? (
                      <span className="text-[11px] font-semibold text-danger bg-danger-light px-1.5 py-0.5 rounded">
                        Zakázán
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold text-success bg-success-light px-1.5 py-0.5 rounded">
                        Aktivní
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right space-x-3">
                    <button
                      type="button"
                      onClick={() => setEditing(u)}
                      className="text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer"
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(u.id)}
                      className="text-sm font-semibold text-danger hover:underline cursor-pointer"
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserModal
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setToast({ msg, kind: 'success' });
            await load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider px-5 py-3">
      {children}
    </th>
  );
}

function ModulesBadges({ modules }: { modules: readonly string[] }) {
  if (!modules || modules.length === 0) {
    return <span className="text-[11px] text-text-muted italic">žádný přístup</span>;
  }
  // Super-admin (má user_admin) zobrazíme jako jediný badge "Vše + Správa",
  // ale jen pokud má i všechny ostatní moduly (typicky vlastník).
  const allOthers = MODULE_IDS.filter((m) => m !== 'user_admin');
  const hasAll = modules.includes('user_admin') && allOthers.every((m) => modules.includes(m));
  if (hasAll) {
    return (
      <span className="text-[11px] font-semibold text-primary bg-primary-50 border border-primary/30 rounded px-1.5 py-0.5">
        Všechny moduly + správa
      </span>
    );
  }
  // Části výroby by přehled zaplavily — shrneme je do jednoho odznaku.
  const vyrobaParts = MODULE_IDS.filter((m) => m.startsWith('vyroba_'));
  const mine = modules.filter((m) => !m.startsWith('vyroba_'));
  const vyrobaCount = modules.filter((m) => m.startsWith('vyroba_')).length;
  return (
    <div className="flex flex-wrap gap-1">
      {vyrobaCount > 0 && vyrobaCount < vyrobaParts.length && (
        <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 border text-text-secondary bg-bg border-border">
          Výroba ({vyrobaCount}/{vyrobaParts.length})
        </span>
      )}
      {mine.map((m) => {
        const label = MODULE_LABELS[m as ModuleId] ?? m;
        const isAdminMod = m === 'user_admin';
        return (
          <span
            key={m}
            className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${
              isAdminMod
                ? 'text-primary bg-primary-50 border-primary/30'
                : 'text-text-secondary bg-bg border-border'
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function UserModal({
  user,
  onClose,
  onSaved,
  onError,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isNew = user === null;
  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [password, setPassword] = useState('');
  const [modules, setModules] = useState<ModuleId[]>(() =>
    ((user?.modules ?? []) as ModuleId[]).filter((m) => MODULE_IDS.includes(m)),
  );
  const [disabled, setDisabled] = useState(!!user?.disabled_at);
  const [saving, setSaving] = useState(false);

  function toggleModule(id: ModuleId, on: boolean) {
    setModules((prev) => (on ? Array.from(new Set([...prev, id])) : prev.filter((m) => m !== id)));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await apiFetch('/api/admin/users', {
          method: 'POST',
          body: { email, name, password, modules },
        });
        onSaved('Uživatel vytvořen');
      } else {
        const patch: Record<string, unknown> = { name, modules, disabled };
        if (password) patch.password = password;
        await apiFetch(`/api/admin/users/${user.id}`, {
          method: 'PATCH',
          body: patch,
        });
        onSaved('Uložení proběhlo');
      }
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-5">
          {isNew ? 'Nový uživatel' : 'Upravit uživatele'}
        </h2>
        <hr className="border-border mb-5" />

        <form onSubmit={submit} className="space-y-4">
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isNew}
              required
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all disabled:opacity-60"
            />
          </Field>
          <Field label="Jméno">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </Field>
          <Field label={isNew ? 'Heslo' : 'Nové heslo (nechej prázdné, pokud měnit nechceš)'}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={isNew}
              autoComplete="new-password"
              minLength={isNew ? 10 : undefined}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <p className="text-xs text-text-muted mt-1.5">Min 10 znaků, alespoň 1 písmeno.</p>
          </Field>
          <Field label="Přístup k modulům">
            <ModuleTree selected={modules} onChange={setModules} />
            <p className="text-xs text-text-muted mt-2">
              „Správa uživatelů" je modul jako každý jiný — kdo ho má, smí přidávat/upravovat
              uživatele (de-facto super-admin).
            </p>
          </Field>
          {!isNew && (
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={disabled}
                onChange={(e) => setDisabled(e.target.checked)}
              />
              Zakázat účet
            </label>
          )}

          <div className="flex gap-3 mt-6">
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-sm font-medium text-text-secondary block mb-1.5">{label}</div>
      {children}
    </div>
  );
}
